import QueryString from "qs";
import { lp } from "../utils/logPrefix";
import { guid } from "../utils/util";
import { withValueVersion } from "./withValueVersion";
import { sentryAssertData } from "../error/wsSentry";
import { BackendResponse } from "./BackendResponse";
import { getNextApiUrl } from "./getNextApiUrl";
import Cookies from "js-cookie";

export interface NextApiParams {
  idConcernOverride?: number;
  showSentryDialogOnError?: boolean;
  method?: "POST" | "GET" | "DELETE";
  mapSentryData?: (data: BackendResponse) => BackendResponse;
  [x: string]: any;
}

interface NextApiHelperParams extends NextApiParams {
  path?: string;
  alreadyAttemptedToGetNewToken?: boolean;
  getData?: () => Promise<any>;
  formData?: FormData;
  headers?: { [key: string]: string };
  letBrowserSetHeaders?: boolean;
  cvpBffCorrelationId?: string;
}

const getNextApiHeaders = (args: NextApiHelperParams) => {
  const headers = new Headers();

  const idConcern = args.idConcernOverride ?? window.cvpIdConcern;
  headers.append("idConcern", (idConcern || 0)?.toString());

  if (args.cvpBffCorrelationId) {
    headers.append("Cvpbffcorrelationid", args.cvpBffCorrelationId);
  }

  if (!args.letBrowserSetHeaders)
    headers.append("Content-Type", "application/json");

  const csrfToken = Cookies.get("XSRF-TOKEN");
  if (csrfToken && !headers.has("X-CSRF-TOKEN")) {
    headers.append("X-CSRF-TOKEN", csrfToken);
  }

  if (args.headers) {
    Object.entries(args.headers).forEach(([key, value]) => {
      if (!value) {
        headers.delete(key);
      } else {
        headers.set(key, value);
      }
    });
  }

  return headers;
};

const getNextApiRequestBody = (args: NextApiHelperParams) => {
  const {
    userToken,
    path,
    showSentryDialogOnError,
    mapSentryData,
    method,
    cvpBffCorrelationId,
    ...rest
  } = args;

  if (method !== "POST") return undefined;

  return rest;
};

const getNextApiRequestParams = (args: NextApiHelperParams) => {
  const {
    userToken,
    path,
    showSentryDialogOnError,
    mapSentryData,
    method,
    cvpBffCorrelationId,
    ...rest
  } = args;

  if (method == "POST") return ""; //changed from !=="GET" to only exclude POST so DELETE requests also gets qs

  return "?" + QueryString.stringify(rest);
};

const getNextApiProps = (args: NextApiHelperParams) => {
  return {
    url: getNextApiUrl(args.path),
    headers: getNextApiHeaders(args),
    requestBody: getNextApiRequestBody(args),
    requestParams: getNextApiRequestParams(args),
  };
};

type DefaultNextApiFetch<R> = (args: NextApiHelperParams) => Promise<R>;

function generateCvpBffCorrelationId() {
  return guid().slice(0, 12);
}

/**
 * Deals with errors on backend and frontend and returns a BackendResponse
 */
async function defaultNextApiFetch<R extends BackendResponse>(
  _args: NextApiHelperParams
): Promise<R> {
  const cvpBffCorrelationId =
    _args.cvpBffCorrelationId ?? generateCvpBffCorrelationId();

  const method = _args.method ?? "POST";

  const args = {
    ..._args,
    method,
    cvpBffCorrelationId,
  };

  const f = async (): Promise<BackendResponse> => {
    try {
      const defaultGetData = async (): Promise<BackendResponse> => {
        const { headers, url, requestBody, requestParams } =
          getNextApiProps(args);

        let body: any = args.formData;
        if (!body) {
          body = requestBody ? JSON.stringify(requestBody) : undefined;
        }

        const result = await fetch(url + requestParams, {
          method,
          body,
          headers,
        });

        if (!result.ok) {
          let _result;

          try {
            _result = await result.text();
          } catch (err) {}

          console.error(lp("dnaf-1"), result.statusText);

          return {
            ok: false,
            statusCode: result.status,
            message: result.statusText,
            elapsedMilliseconds: null,
            valueVersion: null,
            result: _result,
            cvpBffCorrelationId,
          };
        }

        try {
          let parsedJson: any = {};
          try {
            parsedJson = await result.json();
          } catch (e) {}

          let response: any = {
            ok: true,
            statusCode: result.status,
            message: result.statusText,
            elapsedMilliseconds: null,
            valueVersion: null,
            cvpBffCorrelationId,
          };

          if (typeof parsedJson === "object" && !Array.isArray(parsedJson)) {
            response = {
              ...response,
              ...parsedJson,
            };
          } else {
            response = {
              ...response,
              result: parsedJson,
            };
          }

          return response;
        } catch (err) {
          console.error(lp("dnaf-2"), err);

          return {
            ok: false,
            statusCode: 1502,
            message: err?.message,
            result: err,
            elapsedMilliseconds: null,
            valueVersion: null,
            cvpBffCorrelationId,
          };
        }
      };

      const getData = args.getData || defaultGetData;

      const data = await getData();

      return data;
    } catch (err) {
      console.error(lp("dnaf-3"), err);

      return {
        ok: false,
        statusCode: 1503,
        message: err?.message,
        result: err,
        elapsedMilliseconds: null,
        valueVersion: null,
        cvpBffCorrelationId,
      };
    }
  };

  const result = await f();

  if (args.showSentryDialogOnError) {
    sentryAssertData(result, {
      mapSentryData: args.mapSentryData,
    });
  }

  return result as any;
}

export interface IWithNextApiOptions<
  R extends BackendResponse,
  P extends NextApiParams = NextApiParams
> {
  f?: (args: P, defaultFetch: DefaultNextApiFetch<R>) => Promise<R>;
  method?: "POST" | "GET" | "DELETE";
}

export function withNextApi<
  R extends BackendResponse,
  P extends NextApiParams = NextApiParams
>(path: string, options?: IWithNextApiOptions<R, P>) {
  let { f, method = "POST" } = options || {};

  if (!f) {
    f = (args, f) => f(args);
  }

  // deal with errors on backend and frontend and return a BackendResponse with .valueVersion
  return withValueVersion((args: P) =>
    f({ path, ...args }, async (args) => {
      const result: R = await defaultNextApiFetch({ ...args, method });

      if (!result.ok) {
        // throw so that WSErrorContainer can catch it
        // note that react-query is configured to retry 2 times before it allows this error to propagate
        console.error(lp(), result);

        let msg = removeHtml(result.result || result.message);

        if (!msg) {
          msg = getErrorMessageForStatusCode(result.statusCode);
        }

        throw new ApiError(
          msg,
          result.statusCode,
          result.cvpBffCorrelationId,
          result
        );
      }

      return result;
    })
  );
}

function removeHtml(errorMsg: string) {
  errorMsg = errorMsg + "";
  if (
    errorMsg.trim().startsWith("<!DOCTYPE html>") &&
    errorMsg.includes("Webtemp er utilgjengelig")
  ) {
    return "";
  }

  return errorMsg;
}

function getErrorMessageForStatusCode(statusCode: number) {
  switch (statusCode) {
    case 400:
      return "Bad request";
    case 401:
      return "Unauthorized";
    case 403:
      return "Forbidden";
    case 404:
      return "Not found";
    case 500:
      return "Internal server error";
    default:
      return "Error";
  }
}

export class ApiError<R extends BackendResponse> extends Error {
  constructor(
    message,
    statusCode: number,
    cvpBffCorrelationId: string,
    result: R
  ) {
    super(message);
    this.statusCode = statusCode;
    this.cvpBffCorrelationId = cvpBffCorrelationId;
    this.result = result;
  }

  statusCode: number;
  cvpBffCorrelationId: string;
  result: R;

  isApiError = true;
}
